import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "stores/RootStore";
import ServiceStore from "stores/ServiceStore";

interface DeliveryTicket {
  id?: number;
  ticketNumber: string;
  batchTicketId: number | null;
  dispatchId: number | null;
  jobId: number | null;
  customerId: number | null;

  // Delivery Details
  deliveryDate: Date;
  deliveryTime: string;
  truckNumber: string;
  driverName: string;

  // Load Information
  mixDesignName: string;
  yardsDelivered: number;
  slumpOrdered: number;
  slumpDelivered: number;
  airContentOrdered: number;
  airContentDelivered: number;
  temperature: number;
  waterAdded: number; // gallons

  // Batch Information
  batchNumber: string;
  batchTime: string;
  loadStartTime: string;
  loadEndTime: string;

  // Delivery Location
  deliveryAddress: string;
  deliveryCity: string;
  deliveryState: string;
  deliveryZip: string;

  // On-Site Information
  arrivalTime: string;
  pouringStartTime: string;
  pouringEndTime: string;
  departureTime: string;
  totalTimeOnSite: number; // minutes

  // Quality
  slumpTestPerformed: boolean;
  temperatureTestPerformed: boolean;
  airContentTestPerformed: boolean;
  samplesTaken: boolean;
  cylindersMade: number;

  // Customer Acceptance
  customerSignature: string;
  customerPrintedName: string;
  customerTitle: string;
  signatureDate: Date;
  acceptanceNotes: string;

  // Issues & Notes
  weatherConditions: string;
  delayReason: string;
  deliveryNotes: string;
  qualityIssues: string;

  // Billing
  pricePerYard: number;
  deliveryCharge: number;
  additionalCharges: { description: string; amount: number }[];
  totalAmount: number;
  paymentMethod: string;
  paymentStatus: "pending" | "paid" | "invoiced";
}

class DeliveryTicketGenerationWorkflowStore {
  rootStore: RootStore;

  // Observables
  loading = false;
  error: string | null = null;
  success: string | null = null;
  activeStep = 0;
  ticketData: DeliveryTicket;

  constructor(rootStore: RootStore) {
    this.ticketData = this.getDefaultTicket();
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  getDefaultTicket = (): DeliveryTicket => ({
    ticketNumber: this.generateTicketNumber(),
    batchTicketId: null,
    dispatchId: null,
    jobId: null,
    customerId: null,

    deliveryDate: new Date(),
    deliveryTime: new Date().toLocaleTimeString("en-US", { hour12: false }),
    truckNumber: "",
    driverName: "",

    mixDesignName: "",
    yardsDelivered: 0,
    slumpOrdered: 0,
    slumpDelivered: 0,
    airContentOrdered: 0,
    airContentDelivered: 0,
    temperature: 0,
    waterAdded: 0,

    batchNumber: "",
    batchTime: "",
    loadStartTime: "",
    loadEndTime: "",

    deliveryAddress: "",
    deliveryCity: "",
    deliveryState: "",
    deliveryZip: "",

    arrivalTime: "",
    pouringStartTime: "",
    pouringEndTime: "",
    departureTime: "",
    totalTimeOnSite: 0,

    slumpTestPerformed: false,
    temperatureTestPerformed: false,
    airContentTestPerformed: false,
    samplesTaken: false,
    cylindersMade: 0,

    customerSignature: "",
    customerPrintedName: "",
    customerTitle: "",
    signatureDate: new Date(),
    acceptanceNotes: "",

    weatherConditions: "",
    delayReason: "",
    deliveryNotes: "",
    qualityIssues: "",

    pricePerYard: 0,
    deliveryCharge: 0,
    additionalCharges: [],
    totalAmount: 0,
    paymentMethod: "invoice",
    paymentStatus: "pending",
  });

  generateTicketNumber = (): string => {
    const now = new Date();
    const year = now.getFullYear().toString().slice(-2);
    const month = (now.getMonth() + 1).toString().padStart(2, "0");
    const day = now.getDate().toString().padStart(2, "0");
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, "0");
    return `DT${year}${month}${day}-${random}`;
  };

  // Actions
  setLoading = (loading: boolean) => {
    this.loading = loading;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setSuccess = (success: string | null) => {
    this.success = success;
  };

  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    if (this.validateCurrentStep()) {
      this.activeStep++;
    }
  };

  prevStep = () => {
    this.activeStep--;
  };

  updateTicketData = <K extends keyof DeliveryTicket>(
    field: K,
    value: DeliveryTicket[K]
  ) => {
    this.ticketData[field] = value;

    // Auto-calculate total time on site
    if (field === "arrivalTime" || field === "departureTime") {
      this.calculateTimeOnSite();
    }

    // Auto-calculate total amount
    if (field === "pricePerYard" || field === "yardsDelivered" || field === "deliveryCharge") {
      this.calculateTotal();
    }
  };

  addAdditionalCharge = (description: string, amount: number) => {
    this.ticketData.additionalCharges = [
      ...this.ticketData.additionalCharges,
      { description, amount }
    ];
    this.calculateTotal();
  };

  removeAdditionalCharge = (index: number) => {
    this.ticketData.additionalCharges = this.ticketData.additionalCharges.filter(
      (_, i) => i !== index
    );
    this.calculateTotal();
  };

  calculateTotal = () => {
    const materialCost = this.ticketData.yardsDelivered * this.ticketData.pricePerYard;
    const additionalTotal = this.ticketData.additionalCharges.reduce(
      (sum, charge) => sum + charge.amount,
      0
    );
    this.ticketData.totalAmount = materialCost + this.ticketData.deliveryCharge + additionalTotal;
  };

  calculateTimeOnSite = () => {
    if (!this.ticketData.arrivalTime || !this.ticketData.departureTime) return;

    try {
      const arrival = new Date(`2000-01-01T${this.ticketData.arrivalTime}`);
      const departure = new Date(`2000-01-01T${this.ticketData.departureTime}`);
      const diffMs = departure.getTime() - arrival.getTime();
      this.ticketData.totalTimeOnSite = Math.round(diffMs / (60 * 1000)); // minutes
    } catch (e) {
      // Invalid time format
      this.ticketData.totalTimeOnSite = 0;
    }
  };

  // Load dispatch data to pre-fill ticket
  loadDispatchData = (dispatchId: number) => {
    // TODO: Load dispatch data when service is available
    this.ticketData.dispatchId = dispatchId;
  };

  // Validation
  validateCurrentStep = (): boolean => {
    this.setError(null);

    switch (this.activeStep) {
      case 0: // Delivery Information
        if (!this.ticketData.dispatchId) {
          this.setError("Please select a dispatch");
          return false;
        }
        if (!this.ticketData.truckNumber) {
          this.setError("Truck number is required");
          return false;
        }
        if (!this.ticketData.driverName) {
          this.setError("Driver name is required");
          return false;
        }
        break;

      case 1: // Load & Mix Information
        if (!this.ticketData.mixDesignName) {
          this.setError("Mix design name is required");
          return false;
        }
        if (this.ticketData.yardsDelivered <= 0) {
          this.setError("Yards delivered must be greater than zero");
          return false;
        }
        break;

      case 2: // Quality Tests
        if (this.ticketData.slumpTestPerformed && this.ticketData.slumpDelivered === 0) {
          this.setError("Please enter slump test result");
          return false;
        }
        if (this.ticketData.temperatureTestPerformed && this.ticketData.temperature === 0) {
          this.setError("Please enter temperature test result");
          return false;
        }
        if (this.ticketData.samplesTaken && this.ticketData.cylindersMade === 0) {
          this.setError("Please specify number of cylinders made");
          return false;
        }
        break;

      case 3: // Customer Acceptance
        if (!this.ticketData.customerPrintedName) {
          this.setError("Customer name is required");
          return false;
        }
        if (!this.ticketData.customerSignature) {
          this.setError("Customer signature is required");
          return false;
        }
        break;
    }

    return true;
  };

  // Async actions
  loadInitialData = async () => {
    this.setLoading(true);
    this.setError(null);

    try {
      // Load any necessary data
      // TODO: Add data loading when services are available
    } catch (err: any) {
      runInAction(() => {
        this.setError("Failed to load initial data");
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  };

  submitTicket = async () => {
    if (!this.validateCurrentStep()) {
      return;
    }

    this.setLoading(true);
    this.setError(null);

    try {
      // TODO: Create delivery ticket when backend service is available
      // This would create a delivery_tickets record

      runInAction(() => {
        this.setSuccess("Delivery ticket created successfully!");
        this.activeStep = 4; // Success step
      });

    } catch (err: any) {
      runInAction(() => {
        this.setError(err.message || "Failed to create delivery ticket");
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  };

  reset = () => {
    this.loading = false;
    this.error = null;
    this.success = null;
    this.activeStep = 0;
    this.ticketData = this.getDefaultTicket();
  };

  // Computed values
  get slumpMeetsSpec() {
    if (!this.ticketData.slumpTestPerformed) return true;
    const diff = Math.abs(this.ticketData.slumpDelivered - this.ticketData.slumpOrdered);
    return diff <= 1; // Within 1 inch tolerance
  }

  get airContentMeetsSpec() {
    if (!this.ticketData.airContentTestPerformed) return true;
    const diff = Math.abs(this.ticketData.airContentDelivered - this.ticketData.airContentOrdered);
    return diff <= 1.5; // Within 1.5% tolerance
  }

  get allTestsPass() {
    return this.slumpMeetsSpec && this.airContentMeetsSpec;
  }

  get totalTimeOnSiteFormatted() {
    if (this.ticketData.totalTimeOnSite === 0) return "N/A";
    const hours = Math.floor(this.ticketData.totalTimeOnSite / 60);
    const minutes = this.ticketData.totalTimeOnSite % 60;
    return `${hours}h ${minutes}m`;
  }

  get paymentStatusColor() {
    const colors: Record<DeliveryTicket["paymentStatus"], string> = {
      pending: "#ff9800",
      paid: "#4caf50",
      invoiced: "#2196f3",
    };
    return colors[this.ticketData.paymentStatus];
  }
}

export default DeliveryTicketGenerationWorkflowStore;
